import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import './ThemeToggle.css';

const ThemeToggle = ({ className = '', showLabel = true, size = 'medium' }) => {
  const { theme, toggleTheme, isTransitioning } = useTheme();
  const isDark = theme === 'dark';

  const toggleVariants = {
    light: { x: 0 },
    dark: { x: 26 }
  };

  const iconVariants = {
    initial: { scale: 0, rotate: -180 },
    animate: { scale: 1, rotate: 0 },
    exit: { scale: 0, rotate: 180 }
  };

  return (
    <div className={`theme-toggle ${size} ${className}`}>
      {showLabel && (
        <span className="theme-toggle__label">
          {isDark ? 'Dark Mode' : 'Light Mode'}
        </span>
      )}
      
      <motion.button
        className={`theme-toggle__button ${isDark ? 'dark' : 'light'}`}
        onClick={toggleTheme}
        disabled={isTransitioning}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title={`Switch to ${isDark ? 'light' : 'dark'} theme`}
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} theme`}
      >
        {/* Track */}
        <div className="theme-toggle__track">
          {/* Background elements */}
          <div className="theme-toggle__track-bg"></div>
          
          {/* Stars for dark mode */}
          <div className="theme-toggle__stars">
            <span className="star star-1">✦</span>
            <span className="star star-2">✧</span>
            <span className="star star-3">✦</span>
          </div>
          
          {/* Clouds for light mode */}
          <div className="theme-toggle__clouds">
            <span className="cloud cloud-1">☁</span>
            <span className="cloud cloud-2">☁</span>
          </div>
        </div>

        {/* Thumb with icon */}
        <motion.div
          className="theme-toggle__thumb"
          variants={toggleVariants}
          animate={theme}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        >
          <motion.div
            className="theme-toggle__icon"
            key={theme}
            variants={iconVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            {isDark ? (
              // Moon icon for dark mode
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8z"/>
              </svg>
            ) : (
              // Sun icon for light mode
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z"/>
              </svg>
            )}
          </motion.div>
        </motion.div>

        {/* Ripple effect */}
        {isTransitioning && (
          <motion.div
            className="theme-toggle__ripple"
            initial={{ scale: 0, opacity: 0.5 }}
            animate={{ scale: 2, opacity: 0 }}
            transition={{ duration: 0.6 }}
          />
        )}
      </motion.button>
    </div>
  );
};

export default ThemeToggle;
